import type { DateRange, DateValue } from "./types";

/**
 * The range half of a `DateValue`, or undefined when it is holding a single
 * day or a list of them.
 */
export const asDateRange = (
  value: DateValue | undefined
): DateRange | undefined =>
  value && !Array.isArray(value) && !(value instanceof Date)
    ? value
    : undefined;

/**
 * Decide what a click on a range calendar should select, given what was
 * selected before it.
 *
 * react-day-picker only ever *grows* a range: with `{ from, to }` already set,
 * clicking outside it moves whichever end is nearer and keeps the other
 * (`addToRange`, the `from && to` branch). There is no branch that begins a new
 * range, so a viewer who picks Aug 1–5 and then clicks Aug 10 gets Aug 1–10
 * and has no way back to a shorter span.
 *
 * This restores the behaviour every other date-range picker has: a click on a
 * finished range starts the next one. A half-picked or empty range is left
 * exactly as react-day-picker computed it, so closing a range — including
 * clicking the same day twice for a single day — is untouched.
 *
 * Opt-in via `Calendar`'s `restartRangeOnComplete`; see its comment for why it
 * is not the default.
 */
export const resolveRangeSelection = (
  nextRange: DateRange | undefined,
  triggerDate: Date | undefined,
  currentSelection: DateRange | undefined
): DateRange | undefined => {
  const isCurrentComplete = Boolean(
    currentSelection?.from && currentSelection?.to
  );

  if (!isCurrentComplete || !triggerDate) {
    return nextRange;
  }

  return { from: triggerDate, to: undefined };
};
